import type { Tagged, UnwrapTagged } from "@-/com.npmjs.type-fest";

/**
 * Brands a value as a gate: proof that a sometimes-hidden component should render, plus the
 * minimum it needs to render (see R4 in `query-context-null-elimination-plan.md`).
 *
 * A gate type is `Tagged<{ … }, "XGate">`, so it can't be written as an object literal — the only
 * way to obtain one is its `useXGate()` hook, which returns `XGate | null` (`null` = the designed
 * hidden state) and builds the visible case with this function. Call it ONLY from a `useXGate`
 * hook — or a `getXGate(arg)` function when the gate needs a per-item argument (e.g. one gate per
 * list row); anywhere else it forges a gate and defeats the check.
 *
 * @example
 * export type DrillPageGlyphPromptGate = Tagged<{ text: string }, "DrillPageGlyphPromptGate">;
 * export function useDrillPageGlyphPromptGate(): DrillPageGlyphPromptGate | null {
 *   …
 *   return isVisible ? createGate<DrillPageGlyphPromptGate>({ text }) : null;
 * }
 */
export function createGate<Gate extends Tagged<unknown, PropertyKey>>(
  value: UnwrapTagged<Gate>,
): Gate {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the single sanctioned brand cast; every gate is constructed through here.
  return value as Gate;
}
